import {
  AuthIdTokenParsed,
  AuthLoginOptions,
  AuthOptions,
  createAuthClient,
} from "@storebrand-digital/auth";
import { MainPath } from "Constants/constants";
import { getAppPath, getCartIdFromUrl } from "./appUtils";
import { isOfflineMode } from "./isOfflineMode";

const options: AuthOptions = {
  config: {
    url: window.env.KEYCLOAK_URL,
    clientId: window.env.KEYCLOAK_CLIENT,
    realm: window.env.KEYCLOAK_REALM,
  },
  init: {
    scope: window.env.KEYCLOAK_SCOPES,
    onFailureRedirectURL: `${getAppPath()}/${
      MainPath.OFFER
    }/${getCartIdFromUrl()}`,
  },
  login: {
    scope: window.env.KEYCLOAK_SCOPES,
    allowedACR: [
      "https://id.storebrand.no/authn/al3",
      "https://id.storebrand.no/authn/al4",
    ],
  },
  logout: {
    redirectUri: window.env.KEYCLOAK_LOGOUT_URL,
  },
  autoRefresh: true,
};

let auth = createAuthClient(options);

export const initAuth = async (): Promise<void> => {
  if (isOfflineMode()) {
    return;
  }
  await auth.init();
};

export function isAuthenticated(): boolean {
  if (isOfflineMode()) {
    return true;
  }
  return auth.authenticated ?? false;
}

export function getAccessToken(): Promise<string> {
  if (isOfflineMode()) {
    return Promise.resolve("");
  }
  return auth.getToken();
}

export function getIdTokenParsed(): AuthIdTokenParsed | undefined {
  if (isOfflineMode()) {
    return {
      no_nin: "12345612345",
      given_name: "",
      family_name: "",
    } as unknown as AuthIdTokenParsed;
  }
  return isAuthenticated() ? auth.idTokenParsed : undefined;
}

export function getNin(): string {
  if (isOfflineMode()) {
    return "12345612345";
  }
  return auth.nin;
}

export function getCmId(): string {
  if (isOfflineMode()) {
    return "123456789";
  }
  return auth.idTokenParsed.cm_id;
}

export function login(loginOptions?: AuthLoginOptions): Promise<void> {
  if (isOfflineMode()) {
    return Promise.resolve();
  }
  return auth.login({
    ...options.login,
    ...(loginOptions ?? {}),
  });
}

export function logout(): Promise<unknown> {
  if (isOfflineMode()) {
    return Promise.resolve();
  }
  return auth.logout();
}

export async function loginIfNeeded(): Promise<void> {
  if (isOfflineMode()) {
    return Promise.resolve();
  }
  if (!isAuthenticated()) {
    await auth.login();
  }
}

export async function initialize(
  config = options.config,
  initOptions = options.init,
  loginOptions = options.login,
): Promise<unknown> {
  if (isOfflineMode()) {
    return Promise.resolve(true);
  }
  auth = createAuthClient({
    config: config,
    init: initOptions,
    login: loginOptions,
    logout: {
      redirectUri: "",
    },
    autoRefresh: true,
  });
  await auth.init();
  return auth;
}
